import React, { ReactNode } from 'react';
import './ChunkRecoveryBoundary.scss';

export const CHUNK_RECOVERY_STORAGE_KEY = 'hana:chunk-recovery';
export const CHUNK_RECOVERY_STABLE_MS = 60_000;

type ChunkRecoveryMarker = {
  attemptedAt: number;
  route: string;
};

type ChunkRecoveryBoundaryProps = {
  children: ReactNode;
};

type ChunkRecoveryBoundaryState = {
  error: unknown;
};

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  return String(error ?? '');
};

export const isChunkLoadError = (error: unknown): boolean =>
  /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    getErrorText(error),
  );

const parseRecoveryMarker = (
  value: string | null,
): ChunkRecoveryMarker | null => {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as Partial<ChunkRecoveryMarker>;

    if (
      typeof parsed.attemptedAt !== 'number' ||
      !Number.isFinite(parsed.attemptedAt) ||
      typeof parsed.route !== 'string'
    ) {
      return null;
    }

    return {
      attemptedAt: parsed.attemptedAt,
      route: parsed.route,
    };
  } catch {
    return null;
  }
};

export const shouldAttemptChunkRecovery = ({
  error,
  markerValue,
  now,
  route,
}: {
  error: unknown;
  markerValue: string | null;
  now: number;
  route: string;
}): boolean => {
  if (!isChunkLoadError(error)) return false;

  const marker = parseRecoveryMarker(markerValue);

  if (!marker || marker.route !== route) return true;

  return now - marker.attemptedAt >= CHUNK_RECOVERY_STABLE_MS;
};

class ChunkRecoveryBoundary extends React.Component<
  ChunkRecoveryBoundaryProps,
  ChunkRecoveryBoundaryState
> {
  private stableBootTimer?: number;

  state: ChunkRecoveryBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: unknown): ChunkRecoveryBoundaryState {
    return { error };
  }

  componentDidMount(): void {
    this.stableBootTimer = window.setTimeout(() => {
      try {
        window.sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY);
      } catch {
        // Storage can be unavailable in hardened browser contexts. Recovery can
        // still fall back to the visible reload control in that case.
      }
    }, CHUNK_RECOVERY_STABLE_MS);
  }

  componentDidCatch(error: Error): void {
    const route = `${window.location.pathname}${window.location.search}`;
    const now = Date.now();

    let markerValue: string | null = null;

    try {
      markerValue = window.sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY);
    } catch {
      // Treat inaccessible storage as an unmarked attempt. The rendered fallback
      // remains available if the browser also blocks the automatic reload.
    }

    if (!shouldAttemptChunkRecovery({ error, markerValue, now, route })) return;

    try {
      window.sessionStorage.setItem(
        CHUNK_RECOVERY_STORAGE_KEY,
        JSON.stringify({
          attemptedAt: now,
          route,
        } satisfies ChunkRecoveryMarker),
      );
      window.location.reload();
    } catch {
      // The fallback below gives the user an explicit recovery action.
    }
  }

  componentWillUnmount(): void {
    if (this.stableBootTimer !== undefined) {
      window.clearTimeout(this.stableBootTimer);
    }
  }

  private reload = (): void => {
    try {
      window.sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY);
    } catch {
      // Reload is still useful even if storage is unavailable.
    }

    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const deploymentChanged = isChunkLoadError(this.state.error);

    return (
      <main className="chunk-recovery" role="alert">
        <section className="chunk-recovery__card">
          <p className="chunk-recovery__eyebrow">Hana</p>
          <h1 className="chunk-recovery__title">
            {deploymentChanged
              ? 'A newer version of Hana is ready'
              : "Hana couldn't load this screen"}
          </h1>
          <p className="chunk-recovery__body">
            {deploymentChanged
              ? 'Reload to continue where you left off with the latest dashboard.'
              : 'Your work is safe. Reload the dashboard to try this screen again.'}
          </p>
          <button
            className="chunk-recovery__button"
            onClick={this.reload}
            type="button"
          >
            Reload Hana
          </button>
        </section>
      </main>
    );
  }
}

export default ChunkRecoveryBoundary;
